import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { notifyError } from '../../utils/notifyError'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import CustomHeader from '../../components/CustomHeader'
import PAGE_TITLES from '../../consts/titles'
import InstructorSurveyListFeature, { instructorCardWidth } from '../../features/InstructorDashboardFeature'
import { ReportsPageWithProvider } from '../ReportsPageV2'
import SkipNavigation from '../../components/SkipNavigation'
import TermHeader from '../../components/TermHeader'
import { Export } from '../../features/InstructorDashboardFeature/components/Export'
import { DistributionManager, TermsManager, UserManager } from '../../APIManager'
import PageHeader from '../../components/PageHeader'
import { CircularProgress, Typography } from '@mui/material'
import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined'
import BarChartIcon from '@mui/icons-material/BarChart'
import LoadingPageV2 from '../LoadingPageV2'
import Dropdown from '../../components/Dropdown'
import ROUTES from '../../consts/paths'
import { useUserProfile } from '../../hooks/useUserProfile'
import { useUserRole } from '../../hooks/useUserRole'
import { useSandboxReportsView, sandboxReportsRequestParam } from '../../hooks/useSandboxReportsView'
import { type ITerm } from '../../models/Term'
import { type IPagination } from '../../models/DataObject'
import GenericButton from '../../components/GenericButton'
import { ButtonSize, ButtonType } from '../../components/GenericButton/styles'
import CustomTooltip from '../../components/CustomTooltip'
import CustomPagination from '../../components/CustomTable/CustomPagination'
import { grey } from '../../colors'

/**
 * Cards per page. Chosen for consistent paging behaviour rather than to preserve the
 * single-request common case — the backend suggested ~100 so a typical 30-40 section
 * instructor would see no change at all, and that trade was declined deliberately. See BC-3
 * in docs/specs/active/2026-08-19-instructor-dashboard-pagination/spec.md before changing it.
 */
export const INSTRUCTOR_CARDS_PAGE_SIZE = 20

const InstructorDashboardPage: React.FC = () => {
  const [instructorName, setInstructorName] = useState('')
  const [searchParams, setSearchParams] = useSearchParams()
  const { id } = useParams()
  const termId = searchParams.get('termId')
  const seriesId = searchParams.get('projectId')
  const view = searchParams.get('view')
  const objectId = searchParams.get('objectId')
  const { data: userProfile } = useUserProfile()
  const user = userProfile?.user
  const userId = user?.userId
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { isGlobalOrGenericRole } = useUserRole()
  // Seeds `projectId` for the report view below, so it has to read the same world the reports
  // picker is reading.
  const { showSandboxOnly } = useSandboxReportsView()

  // Resolve the highest-scoped division for instructor report view, mirroring
  // the division report default-navigation behaviour.  Only runs when entering
  // report view without an explicit objectId already in the URL.
  const cachedTopBarDivisions = queryClient.getQueryData<any[]>(['topBarDivisions'])
  const { data: topBarDivisionsApi } = useQuery({
    queryKey: ['topBarDivisions'],
    queryFn: async () => (await UserManager.getTopBarDivisions()) as any,
    enabled: view === 'report' && !objectId && !cachedTopBarDivisions && isGlobalOrGenericRole
  })
  const topBarDivisions = cachedTopBarDivisions ?? topBarDivisionsApi

  useEffect(() => {
    if (view !== 'report' || objectId) return
    const firstDivisionId = Array.isArray(topBarDivisions) && topBarDivisions[0]?.divisionId
    if (firstDivisionId) {
      setSearchParams(prev => { prev.set('objectId', firstDivisionId); return prev }, { replace: true })
    }
  }, [view, objectId, topBarDivisions, setSearchParams])

  const {
    data: terms = [],
    isLoading: termsLoading,
    isError: isTermsError,
    error: termsError
  } = useQuery({
    queryKey: ['terms', id ?? userId],
    staleTime: 1000,
    queryFn: async () => (await TermsManager.getAllTerms({ pageSize: 200, userId: id ?? userId, isInstructor: true })).data,
    retry: false,
    enabled: Boolean(id ?? userId)
  })

  // Paginated because this list is unbounded by the instructor's section count: a reviewer
  // assigned to every section of a project (~6,000 at one customer) took the endpoint past a
  // gateway timeout. The page number is part of the query key so pages cache independently.
  // Spec: docs/specs/active/2026-08-19-instructor-dashboard-pagination/spec.md (BC-3)
  // The page number is stored with the term it belongs to and the effective value derived,
  // rather than reset from an effect. An effect runs after render, so the query would fire
  // once for "page 3 of the new term" before the reset landed — a wasted request, and a
  // flash of the wrong page (or an empty one, if the new term has fewer pages).
  // Identity of the list currently on screen. Everything derived from a page of that list is
  // scoped to this, not to the term alone: the query is keyed by instructor as well as term
  // (`['instructorDistributions', id ?? 'self', termId, pageNumber]`), and the route mounts this
  // page once for `/instructor/surveys/:id?` (`src/pages/index.tsx`), so React reuses the same
  // component instance across `:id` changes rather than remounting. Anything keyed only by term
  // therefore survives a switch to a different instructor.
  const listScope = `${id ?? 'self'}:${termId ?? ''}`

  const [pageState, setPageState] = useState<{ scope: string, pageNumber: number }>({
    scope: listScope,
    pageNumber: 1
  })
  const pageNumber = pageState.scope === listScope ? pageState.pageNumber : 1
  const goToPage = (page: number): void => { setPageState({ scope: listScope, pageNumber: page }) }

  const {
    data: distributionsPage,
    isLoading: distributionsLoading,
    isError: isDistributionsError,
    error: distributionsError
  } = useQuery({
    queryKey: ['instructorDistributions', id ?? 'self', termId, pageNumber],
    queryFn: async () => {
      const params = { termId, pageNumber, pageSize: INSTRUCTOR_CARDS_PAGE_SIZE }
      return id
        ? await DistributionManager.getInstructorDistributionsById(id, params)
        : await DistributionManager.getInstructorDistributions(params)
    },
    enabled: Boolean(termId && (id || id === undefined)),
    retry: false
  })

  const instructorDistributions = distributionsPage?.data ?? []
  const pagination = distributionsPage?.pagination

  // React Query drops `data` on error, so a failed page would otherwise unmount the pager and
  // leave the instructor stranded on the page that failed, with no in-page way back to one
  // that works. Hold the last pagination successfully read so the pager survives the failure.
  // Assigning during render is safe here: it is a pure carry-forward of a derived value, not a
  // state update.
  //
  // Stored *with* its `listScope`, and only reused while that still matches — the same pairing
  // `pageState` uses. Keyed on the term alone it would keep serving the previous list's
  // `numPages` and `totalResults` after a change whose own request failed or returned no
  // pagination block, describing a list the viewer is no longer looking at. That applies to a
  // term change *and* to switching between instructors within one term, which is the case the
  // route's reuse of this component makes reachable.
  const lastPaginationRef = useRef<{ scope: string, pagination: IPagination } | undefined>(undefined)
  if (pagination != null) lastPaginationRef.current = { scope: listScope, pagination }
  const carriedPagination = lastPaginationRef.current?.scope === listScope
    ? lastPaginationRef.current.pagination
    : undefined
  const effectivePagination = pagination ?? carriedPagination

  // Reads the carried value, not the raw one: on a failed page `pagination` is undefined and
  // the card list is empty, so a raw read gives 0 and disables Export as though the instructor
  // had no surveys at all. The export is term-scoped and ignores the current page, so the term
  // total is the right input — and it is still known.
  const totalResults = effectivePagination?.totalResults ?? instructorDistributions.length

  // The report view is scoped to a single survey project, so switching into it has to pick
  // one. Read it from the term's project list rather than from the card list: a term can hold
  // several projects, so the first card only ever named an arbitrary one — and once the card
  // list is paginated it would name "the first card of whichever page you were on," silently
  // changing which project the report describes as the instructor pages.
  //
  // Key and params match ProjectAndTermHeader's exactly
  // (`['ReportsProjects', activeFilterTermId, showSandboxOnly]`) so the report header and this
  // page share one cache entry instead of fetching twice.
  //
  // CR-341: the sandbox preference belongs here for the same reason the header carries it. This
  // query exists only to seed the report view's `projectId`, so seeding it from a differently
  // filtered list is how you land on a project the picker will not offer — pointing the report at
  // a rehearsal while the picker lists real projects, or the reverse. A fixed value cannot be
  // right in both directions; matching the switch is the only answer that holds.
  const {
    data: termProjectsResponse,
    isLoading: termProjectsLoading
  } = useQuery({
    queryKey: ['ReportsProjects', termId ?? undefined, showSandboxOnly],
    queryFn: async () => await DistributionManager.getAllDistributionSeries({
      activeTerms: true,
      ...(termId != null ? { term_id: termId } : {}),
      ...sandboxReportsRequestParam(showSandboxOnly)
    }),
    retry: false,
    enabled: Boolean(termId)
  })

  // CDG rows nest the entity under `data`, matching ProjectAndTermHeader's `p.data` access.
  const firstTermProjectId = termProjectsResponse?.data?.[0]?.data?.distributionSeriesId

  // Every query on the report page is gated on `projectId` (`enabled: !!seriesId`), so arriving
  // there without one renders a blank report and no error. The Report View button seeds it, but
  // that is not the only way in: `view` defaults to `report` for an admin viewing an instructor
  // and for a past term, and a shared URL can land on report view directly. Neither of those
  // paths ever set it. Seed wherever report view is reached without one, rather than patching
  // each entry point.
  //
  // This gap predates pagination — the old toggle was the only seeding site too — but it used
  // to be masked, because the completion figure fell back to summing the card list. That
  // fallback is gone (BC-6), so the blank is now total.
  useEffect(() => {
    if (view !== 'report' || seriesId) return
    if (firstTermProjectId == null) return
    setSearchParams(prev => { prev.set('projectId', firstTermProjectId); return prev }, { replace: true })
  }, [view, seriesId, firstTermProjectId, setSearchParams])

  const {
    data: facultyData,
    isLoading: facultyLoading,
    isError: isFacultyError,
    error: facultyError
  } = useQuery({
    queryKey: ['faculty', id],
    queryFn: async () => (await UserManager.getFacultyAndStaffByIdApi(id ?? '')).data,
    enabled: Boolean(id)
  })

  useEffect(() => {
    if (isTermsError) notifyError(termsError, { fallbackMessage: 'Failed to load terms. Please try again.' })
  }, [isTermsError])

  useEffect(() => {
    if (isDistributionsError) notifyError(distributionsError, { fallbackMessage: 'Failed to load surveys. Please try again.' })
  }, [isDistributionsError])

  useEffect(() => {
    if (isFacultyError) notifyError(facultyError, { fallbackMessage: 'Failed to load instructor data. Please try again.' })
  }, [isFacultyError])

  // Export used to be gated on "some card is Closed or Live", which is a whole-list predicate
  // and cannot be evaluated from a single page. It was also guarding a server-side, term-scoped
  // export (/api/users/<id>/reports/export/...) that exports every section regardless of which
  // page is on screen, so the gate was a courtesy rather than a safeguard. Now: enabled
  // whenever the term has anything to export.
  // Spec: docs/specs/active/2026-08-19-instructor-dashboard-pagination/spec.md (BC-4)
  const exportButtonProps = { disabled: totalResults === 0 }
  useEffect(() => {
    if (view) return
    if (!terms || terms.length === 0) return
    const selectedTerm = terms.find((t: ITerm) => t.termId === termId)
    const isPastTerm = selectedTerm && selectedTerm.endTimestamp < new Date().toISOString()
    const defaultView = (id || isPastTerm) ? 'report' : 'card'
    setSearchParams(prev => { prev.set('view', defaultView); return prev }, { replace: true })
  }, [terms, termId, view, id])

  useEffect(() => {
    if (facultyData) {
      setInstructorName(`${facultyData.firstName} ${facultyData.lastName}`)
    } else if (!id && user) {
      setInstructorName(user?.name ?? 'Courses')
    }
  }, [facultyData, user, id])

  // Derive objectId immediately from cache rather than waiting for the setSearchParams
  // effect to round-trip through the URL, so SectionTable gets the scope on first render.
  const effectiveObjectId = objectId ?? (Array.isArray(topBarDivisions) ? topBarDivisions[0]?.divisionId : undefined)

  const viewToggle = useMemo(() => (
    <Box display="flex" alignItems="center" gap={1}>
      <Box sx={{ display: 'flex', border: `1px solid ${grey[350]}`, borderRadius: '8px' }}>
        <CustomTooltip title="Survey Card View">
          <GenericButton
            icon={<SpaceDashboardOutlinedIcon />}
            onClick={() => { setSearchParams(prev => { prev.set('view', 'card'); return prev }) }}
            buttonType={ButtonType.Ghost}
            buttonSize={ButtonSize.Large}
            sx={{ borderRight: `1px solid ${grey[350]}`, borderRadius: '8px 0 0 8px', background: `${grey[0]} !important` }}
          />
        </CustomTooltip>
        <CustomTooltip title="Report View">
          <GenericButton
            icon={<BarChartIcon />}
            onClick={() => { setSearchParams(prev => { prev.set('view', 'report'); return prev }) }}
            buttonType={ButtonType.Ghost}
            buttonSize={ButtonSize.Large}
            sx={{ borderRadius: '0 8px 8px 0', background: `${grey[100]} !important` }}
          />
        </CustomTooltip>
      </Box>
      {/* Saved Comments moved into ReportsControls' single ⋯ on 2026-09-23. It had its own
          dropdown here, which put a second unlabelled ⋯ beside the reports one. The card view
          keeps its own menu below — this toggle only renders on the report view. */}
    </Box>
  ), [id, setSearchParams, navigate])

  // The card list is intentionally not passed through: the report view no longer derives
  // anything from it (see BC-6), and handing it a single page of cards would invite exactly
  // the page-scoped aggregate this work removed.
  const instructorModeConfig = useMemo(() => ({
    instructorId: id,
    selfUserId: userId,
    instructorName,
    objectId: effectiveObjectId,
    extraControls: viewToggle
  }), [id, userId, instructorName, effectiveObjectId, viewToggle])

  if (facultyLoading || termsLoading) {
    return <LoadingPageV2 />
  }

  const menuItems = [
    { text: 'Saved Comments', action: () => { navigate(ROUTES.SAVED_COMMENTS) } }
  ]

  if (view === 'report') {
    return (
      <ReportsPageWithProvider instructorMode={instructorModeConfig} />
    )
  }

  return (
    <Box>
      <SkipNavigation />
      <CustomHeader info={distributionsLoading ? PAGE_TITLES.INSTRUCTOR_DASHBOARD : instructorName} />
      <PageHeader
        title={instructorName}
        pageTitleAriaLabel={`Survey results for ${instructorName}`}
        controls={
          <>
            <TermHeader showSeries={false} terms={terms} termsLoading={termsLoading}>
              <Export disabled={exportButtonProps.disabled} />
            </TermHeader>
            <Box ml={2} sx={{ display: 'flex', border: `1px solid ${grey[350]}`, borderRadius: '8px' }}>
              <CustomTooltip title="Survey Card View">
                <GenericButton
                  icon={<SpaceDashboardOutlinedIcon />}
                  onClick={() => { setSearchParams(prev => { prev.set('view', 'card'); return prev }) }}
                  buttonType={ButtonType.Ghost}
                  buttonSize={ButtonSize.Medium}
                  sx={{ borderRight: `1px solid ${grey[350]}`, borderRadius: '8px 0 0 8px', background: view === 'card' ? `${grey[100]} !important` : `${grey[0]} !important` }}
                />
              </CustomTooltip>
              <CustomTooltip title="Report View">
                <GenericButton
                  icon={<BarChartIcon />}
                  // Wait rather than navigate to a report with no project: the report's
                  // queries are gated on `projectId`, so switching before the project list
                  // resolves would land on an empty report. The query above fires on mount,
                  // so in practice this is warm before the button is reachable.
                  disabled={termProjectsLoading}
                  onClick={() => {
                    if (id || !userId) {
                      setSearchParams(prev => {
                        prev.set('view', 'report')
                        if (firstTermProjectId != null) prev.set('projectId', firstTermProjectId)
                        return prev
                      })
                    } else {
                      const sp = new URLSearchParams(searchParams)
                      sp.set('view', 'report')
                      if (firstTermProjectId != null) sp.set('projectId', firstTermProjectId)
                      const firstDivisionId = Array.isArray(topBarDivisions) ? topBarDivisions[0]?.divisionId : undefined
                      if (firstDivisionId != null && !sp.get('objectId')) sp.set('objectId', firstDivisionId)
                      navigate(`${ROUTES.INSTRUCTOR_SURVEYS}/${userId}?${sp.toString()}`)
                    }
                  }}
                  buttonType={ButtonType.Ghost}
                  buttonSize={ButtonSize.Medium}
                  sx={{ borderRadius: '0 8px 8px 0', background: view === 'report' ? `${grey[100]} !important` : `${grey[0]} !important` }}
                />
              </CustomTooltip>
            </Box>
            <Box ml={2}><Dropdown dropdownType='action' menuItems={menuItems} /></Box>
          </>
        }
        isCompactOnly
      />
      {distributionsLoading
        ? <Box display="flex" justifyContent="center" alignItems={'center'} height={400} mt={2}>
            <CircularProgress />
          </Box>
        : <>
            {isDistributionsError
              // Not the empty state: with no data, the card list would render "no surveys yet",
              // which asserts something false about the term when the request simply failed.
              ? <Box
                  display="flex" flexDirection="column" alignItems="center" gap={2}
                  maxWidth={instructorCardWidth} width='100%' margin='auto' mt={8} textAlign="center"
                >
                  <Typography fontSize="16px" fontWeight={500} color={grey[950]}>
                    We couldn&apos;t load {pageNumber > 1 ? `page ${pageNumber} of your surveys` : 'your surveys'}.
                  </Typography>
                  {pageNumber > 1 && (
                    <GenericButton
                      buttonType={ButtonType.Secondary}
                      buttonSize={ButtonSize.Medium}
                      label="Back to first page"
                      onClick={() => { goToPage(1) }}
                    />
                  )}
                </Box>
              : <InstructorSurveyListFeature
                  list={instructorDistributions}
                  pageNumber={pageNumber}
                  pageSize={INSTRUCTOR_CARDS_PAGE_SIZE}
                  totalResults={totalResults}
                />
            }
            {/* Only when there is more than one page: an instructor with a handful of
                sections should see the list exactly as they do today.
                Uses the last known pagination so a failed page keeps the pager reachable
                rather than stranding the instructor on the page that failed. */}
            {effectivePagination != null && effectivePagination.numPages > 1 && (
              <Box maxWidth={instructorCardWidth} width='100%' margin='auto'>
                <CustomPagination
                  numPages={effectivePagination.numPages}
                  pageNumber={pageNumber}
                  pageSize={effectivePagination.pageSize}
                  totalResults={effectivePagination.totalResults}
                  handlePageChange={goToPage}
                  showPageSizeOptions={false}
                />
              </Box>
            )}
          </>
      }
    </Box>
  )
}

export default InstructorDashboardPage
